import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project } from '../../types';
import { dossierAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';
import { Zoekterm, Operator, OPERATORS, PERIODES, STANDAARD_PERIODE, schoon, sorteer, bouwZoekvraag, leesbareZoekvraag, naarZoekmachine, naarNederlands, leesTermen } from './zoekvraag';

interface MediaDashboardModuleProps {
  project: Project;
}

const SENT_COLORS: Record<string, string> = { positief: '#1f9d55', negatief: '#b91c1c', gemengd: '#d97706', neutraal: '#6b7280' };
const sentColor = (v?: string): string => SENT_COLORS[String(v || '').toLowerCase()] || '#6b7280';
const sentBg: Record<string, string> = { positief: '#eafaf0', negatief: '#fdecec', gemengd: '#fef4e6', neutraal: '#f1f3f6' };
const scoreColor = (n?: number): string => (typeof n === 'number' && n >= 20 ? '#1f9d55' : typeof n === 'number' && n <= -20 ? '#b91c1c' : '#d97706');

const card: React.CSSProperties = { background: '#fff', border: '1px solid #ececf1', borderRadius: 14, padding: 20 };
const extIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
);

const SentPill: React.FC<{ v?: string }> = ({ v }) => (
  <span style={{ fontSize: 11.5, fontWeight: 700, color: sentColor(v), background: sentBg[String(v || 'neutraal').toLowerCase()] || '#f1f3f6', borderRadius: 999, padding: '2px 9px', textTransform: 'capitalize' }}>{v || 'neutraal'}</span>
);

// Uniforme thumbnail: Open Graph-afbeelding → bronlogo (favicon) → auteur-avatar → icoon.
const Thumb: React.FC<{ image?: string; favicon?: string; avatar?: string; w: number; h: number }> = ({ image, favicon, avatar, w, h }) => {
  const [e1, setE1] = useState(false);
  const [e2, setE2] = useState(false);
  const [e3, setE3] = useState(false);
  if (image && !e1) return <img src={image} alt="" onError={() => setE1(true)} style={{ width: w, height: h, objectFit: 'cover', flexShrink: 0, display: 'block' }} />;
  if (favicon && !e2) return (
    <span style={{ width: w, height: h, flexShrink: 0, background: '#f4f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img src={favicon} alt="" onError={() => setE2(true)} style={{ width: 32, height: 32, borderRadius: 7 }} />
    </span>
  );
  if (avatar && !e3) return <img src={avatar} alt="" onError={() => setE3(true)} style={{ width: w, height: h, objectFit: 'cover', flexShrink: 0, display: 'block' }} />;
  return (
    <span style={{ width: w, height: h, flexShrink: 0, background: '#f4f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#c3c9d4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v10a2 2 0 0 0 2 2z" /><line x1="6" y1="9" x2="13" y2="9" /><line x1="6" y1="13" x2="13" y2="13" /></svg>
    </span>
  );
};

// Eén uniforme berichtkaart voor zowel Nieuwsberichten als Mediamonitoring (Coosto).
// Coosto levert per bericht het kanaal (sourceType); zo is zichtbaar dat social media meegaan.
const PLATFORMS: Record<string, { label: string; kleur: string; social: boolean }> = {
  twitter: { label: 'X', kleur: '#111827', social: true },
  x: { label: 'X', kleur: '#111827', social: true },
  facebook: { label: 'Facebook', kleur: '#1877F2', social: true },
  instagram: { label: 'Instagram', kleur: '#C13584', social: true },
  linkedin: { label: 'LinkedIn', kleur: '#0A66C2', social: true },
  bluesky: { label: 'Bluesky', kleur: '#0085FF', social: true },
  tiktok: { label: 'TikTok', kleur: '#111827', social: true },
  youtube: { label: 'YouTube', kleur: '#DC2626', social: true },
  reddit: { label: 'Reddit', kleur: '#FF4500', social: true },
  forum: { label: 'Forum', kleur: '#6D28D9', social: true },
  blog: { label: 'Blog', kleur: '#B45309', social: false },
  news: { label: 'Nieuws', kleur: '#475569', social: false },
  web: { label: 'Web', kleur: '#64748B', social: false },
};
const platformInfo = (p?: string) => {
  const k = String(p || 'web').toLowerCase();
  return PLATFORMS[k] || { label: k.charAt(0).toUpperCase() + k.slice(1), kleur: '#64748B', social: false };
};
const PlatformChip: React.FC<{ platform?: string }> = ({ platform }) => {
  const info = platformInfo(platform);
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: info.kleur, borderRadius: 4, padding: '1px 6px', letterSpacing: 0.2 }}>{info.label}</span>;
};

// Eerder opgeslagen Coosto-berichten kunnen een datum in 1970 hebben (tijd in seconden als ms gelezen); die tonen we niet.
const geldigeDatum = (v?: string): boolean => { if (!v) return false; const d = new Date(v); return !isNaN(d.getTime()) && d.getFullYear() >= 2000; };

const MessageCard: React.FC<{ msg: any; w: number; h: number }> = ({ msg, w, h }) => (
  <a href={msg.url || undefined} target={msg.url ? '_blank' : undefined} rel="noopener noreferrer"
    style={{ display: 'flex', alignItems: 'stretch', border: '1px solid #ececf1', borderRadius: 12, textDecoration: 'none', background: '#fff', overflow: 'hidden', cursor: msg.url ? 'pointer' : 'default' }}>
    <Thumb image={msg.image} favicon={msg.favicon} avatar={msg.avatar} w={w} h={h} />
    <span style={{ flex: 1, minWidth: 0, padding: '11px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <span style={{ fontSize: 14.5, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as React.CSSProperties}>{msg.title}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, fontSize: 12, color: '#9aa1b0', flexWrap: 'wrap' }}>
        <SentPill v={msg.sentiment} />
        {msg.platform ? <PlatformChip platform={msg.platform} /> : null}
        {msg.source ? <span>· {msg.source}</span> : null}
        {geldigeDatum(msg.date) ? <span>· {new Date(msg.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</span> : null}
        {typeof msg.reach === 'number' && msg.reach > 0 ? <span>· bereik {msg.reach.toLocaleString('nl-NL')}</span> : null}
      </span>
    </span>
    <span style={{ display: 'flex', alignItems: 'flex-start', padding: 10, flexShrink: 0 }}>
      <span style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #eceef2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1e3a2f' }}>{extIcon}</span>
    </span>
  </a>
);

// Compacte sentiment-header (badge + score-balk + verdeling) voor een resultatenset.
const SentimentHeader: React.FC<{ snap: any; isMobile: boolean }> = ({ snap, isMobile }) => {
  const items: any[] = Array.isArray(snap?.items) ? snap.items : [];
  const breakdown = { positief: 0, neutraal: 0, negatief: 0 };
  items.forEach((it) => { const s = String(it.sentiment || 'neutraal').toLowerCase(); if (s in breakdown) (breakdown as any)[s] += 1; });
  const score = typeof snap?.score === 'number' ? snap.score : 0;
  const markerPos = Math.max(0, Math.min(100, (score + 100) / 2));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 14, marginBottom: 16 }}>
      <div style={card}>
        <div style={{ fontSize: 12.5, color: '#8a90a0', marginBottom: 8 }}>Overall sentiment</div>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#fff', background: sentColor(snap.overall), borderRadius: 999, padding: '5px 14px', textTransform: 'capitalize', display: 'inline-block' }}>{snap.overall}</span>
      </div>
      <div style={card}>
        <div style={{ fontSize: 12.5, color: '#8a90a0', marginBottom: 8 }}>Sentimentscore</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor(score), marginBottom: 8 }}>{score > 0 ? `+${score}` : score}</div>
        <div style={{ position: 'relative', height: 8, borderRadius: 999, background: 'linear-gradient(90deg,#f5b5b5,#eef0f4,#a9e2c1)' }}>
          <div style={{ position: 'absolute', left: `calc(${markerPos}% - 6px)`, top: -2, width: 12, height: 12, borderRadius: '50%', background: scoreColor(score), border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9aa1b0', marginTop: 4 }}><span>−100</span><span>0</span><span>+100</span></div>
      </div>
      <div style={card}>
        <div style={{ fontSize: 12.5, color: '#8a90a0', marginBottom: 8 }}>{items.length} bericht(en)</div>
        <div style={{ display: 'flex', gap: 12, fontSize: 13, fontWeight: 600 }}>
          <span style={{ color: '#1f9d55' }}>● {breakdown.positief}</span>
          <span style={{ color: '#6b7280' }}>● {breakdown.neutraal}</span>
          <span style={{ color: '#b91c1c' }}>● {breakdown.negatief}</span>
        </div>
        <div style={{ fontSize: 11.5, color: '#9aa1b0', marginTop: 8 }}>Bijgewerkt {snap.opgehaaldOp ? new Date(snap.opgehaaldOp).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</div>
      </div>
    </div>
  );
};

/** Kleur per operator: OF neutraal, EN groen (moet erin), NIET rood (uitsluiten). */
const OPERATOR_KLEUR: Record<Operator, { tekst: string; vlak: string; rand: string }> = {
  OF: { tekst: '#6b7280', vlak: '#f3f4f6', rand: '#e5e7eb' },
  EN: { tekst: BRAND.groen, vlak: '#e6f1ea', rand: '#c5dccd' },
  NIET: { tekst: '#b42318', vlak: '#fef3f2', rand: '#fecdca' },
};
const operatorStijl = (op: Operator): React.CSSProperties => ({
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: OPERATOR_KLEUR[op].tekst, background: OPERATOR_KLEUR[op].vlak,
  border: `1px solid ${OPERATOR_KLEUR[op].rand}`, borderRadius: 6, padding: '2px 5px', fontFamily: 'inherit',
});

export const MediaDashboardModule: React.FC<MediaDashboardModuleProps> = ({ project }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [base, setBase] = useState<any>({});
  const [nieuws, setNieuws] = useState<any | null>(null);
  const [coosto, setCoosto] = useState<any | null>(null);
  const [keywords, setKeywords] = useState('');          // zelf geschreven zoekvraag (OF/EN/NIET)
  const [termen, setTermen] = useState<Zoekterm[]>([]);    // zoekbouwer: per term OF / EN / NIET
  const [vrij, setVrij] = useState(false);                 // true = zelf een zoekvraag schrijven
  const [nieuweTerm, setNieuweTerm] = useState('');
  const [periode, setPeriode] = useState<number>(STANDAARD_PERIODE); // dagen terug
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [showAllNews, setShowAllNews] = useState(false);
  // AI-voorstellen voor zoektermen (uit projectverkenning + omgevingsanalyse); klikken voegt toe.
  const [aiSuggesties, setAiSuggesties] = useState<{ term: string; waarom: string }[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [showAllCoosto, setShowAllCoosto] = useState(false);
  const [activeTab, setActiveTab] = useState<'coosto' | 'nieuws'>('coosto');

  // Standaard: projectnaam en plaats als twee losse termen (niet als één lange zin die overal in moet staan).
  const defaultTermen: Zoekterm[] = Array.from(new Set([project.name, project.location].filter((x) => x && x !== 'Unknown').map((x) => String(x).trim())))
    .map((tekst) => ({ tekst, op: 'OF' as Operator }));
  /** Zoekinstellingen uit het dossier terugzetten: de opgeslagen termen, anders de opgeslagen zoekvraag ontleden. */
  const zetZoekvraag = (content: any) => {
    setPeriode(PERIODES.some((p) => p.dagen === content?.nieuwsPeriode) ? content.nieuwsPeriode : STANDAARD_PERIODE);
    const opgeslagen = Array.isArray(content?.nieuwsZoektermen) ? content.nieuwsZoektermen.filter((t: any) => t && schoon(t.tekst)) : [];
    if (opgeslagen.length) { setVrij(false); setTermen(sorteer(opgeslagen.map((t: any) => ({ tekst: schoon(t.tekst), op: (['OF', 'EN', 'NIET'].includes(t.op) ? t.op : 'OF') as Operator })))); return; }
    const q: string = content?.nieuwsKeywords || '';
    const gelezen = q ? leesTermen(q) : defaultTermen;
    if (gelezen === null) { setVrij(true); setKeywords(naarNederlands(q)); setTermen([]); }
    else { setVrij(false); setTermen(sorteer(gelezen)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const section = res?.dossier?.sections?.omgevingsanalyse;
      const content = (section && section.content) || {};
      setBase(content);
      setNieuws(content.nieuwsSentiment || null);
      setCoosto(content.coostoSentiment || null);
      zetZoekvraag(content);
      setLocked(section?.status === 'locked');
    } catch (e) {
      zetZoekvraag({});
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  // Wat naar Coosto en Google Nieuws gaat (OR / AND / -), en dezelfde vraag leesbaar in het Nederlands.
  const zoekvraag = vrij ? naarZoekmachine(keywords) : bouwZoekvraag(termen);
  const zoekvraagLeesbaar = vrij ? naarNederlands(zoekvraag) : leesbareZoekvraag(termen);

  const zoek = async () => {
    const q = zoekvraag;
    if (!q) { toast.error('Geen zoekvraag', 'Voeg minstens één term toe die wél moet voorkomen (OF of EN).'); return; }
    setBusy(true);
    try {
      const [newsR, coostoR] = await Promise.allSettled([
        dossierAPI.newsSentiment(project.id, q, periode),
        dossierAPI.coostoSentiment(project.id, q, periode),
      ]);
      const nres = newsR.status === 'fulfilled' ? newsR.value : null;
      const cres = coostoR.status === 'fulfilled' ? coostoR.value : null;
      const newsSnap = nres ? { items: nres.items || [], overall: nres.overall, score: nres.score, samenvatting: nres.samenvatting, opgehaaldOp: nres.opgehaaldOp, query: nres.query, fetchError: nres.fetchError || null } : nieuws;
      const coostoSnap = cres ? { items: cres.items || [], overall: cres.overall, score: cres.score, samenvatting: cres.samenvatting, opgehaaldOp: cres.opgehaaldOp, query: cres.query, totalReach: cres.totalReach, totalMessages: cres.totalMessages, fetchError: cres.fetchError || null } : coosto;
      setNieuws(newsSnap);
      setCoosto(coostoSnap);
      setShowAllNews(false);
      setShowAllCoosto(false);
      if (!locked) {
        try {
          const zoekinstellingen = { nieuwsKeywords: q, nieuwsZoektermen: vrij ? [] : termen, nieuwsPeriode: periode };
          await dossierAPI.saveSection(project.id, 'omgevingsanalyse', { ...base, nieuwsSentiment: newsSnap, coostoSentiment: coostoSnap, ...zoekinstellingen });
          setBase((b: any) => ({ ...b, nieuwsSentiment: newsSnap, coostoSentiment: coostoSnap, ...zoekinstellingen }));
        } catch (e) { /* niet kritisch */ }
      }
      const nCount = newsSnap?.items?.length || 0;
      const cCount = coostoSnap?.items?.length || 0;
      toast.success('Opgehaald', `${nCount} nieuws · ${cCount} via Coosto.`);
    } catch (e: any) {
      toast.error('Ophalen mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setBusy(false); }
  };

  const heeftTerm = (t: string): boolean => {
    const k = schoon(t).toLowerCase();
    return vrij ? keywords.toLowerCase().includes(k) : termen.some((x) => x.tekst.toLowerCase() === k);
  };
  /** Term(en) toevoegen; een suggestie met OR erin wordt in losse termen gesplitst. Standaard als OF. */
  const addTerm = (t: string, op: Operator = 'OF') => {
    const delen = t.split(/\s+(?:OR|OF)\s+/).map(schoon).filter(Boolean);
    if (vrij) {
      setKeywords((k) => [k.trim(), ...delen.filter((d) => !k.toLowerCase().includes(d.toLowerCase())).map((d) => (/\s/.test(d) ? `"${d}"` : d))].filter(Boolean).join(' OF '));
    } else {
      setTermen((cur) => sorteer([...cur, ...delen.filter((d) => !cur.some((x) => x.tekst.toLowerCase() === d.toLowerCase())).map((tekst) => ({ tekst, op }))]));
    }
  };
  const verwijderTerm = (tekst: string) => setTermen((cur) => cur.filter((x) => x.tekst !== tekst));
  const zetOperator = (tekst: string, op: Operator) => setTermen((cur) => sorteer(cur.map((x) => (x.tekst === tekst ? { ...x, op } : x))));
  const naarVrij = () => { setKeywords(leesbareZoekvraag(termen)); setVrij(true); };
  const naarTermen = () => {
    const gelezen = leesTermen(naarZoekmachine(keywords));
    if (gelezen === null) { toast.error('Niet om te zetten', 'Deze zoekvraag is te samengesteld voor losse termen; die kun je alleen als zelf geschreven zoekvraag bewerken.'); return; }
    setTermen(sorteer(gelezen)); setVrij(false);
  };

  const stelZoektermenVoor = async () => {
    setAiBusy(true);
    try {
      const res = await dossierAPI.suggestZoektermen(project.id, zoekvraag);
      const lijst = Array.isArray(res?.zoektermen) ? res.zoektermen : [];
      setAiSuggesties(lijst);
      if (lijst.length === 0) toast.error('Geen suggesties', 'De AI vond geen aanvullende zoektermen. Vul de projectverkenning verder in.');
    } catch (e: any) {
      toast.error('Suggesties mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setAiBusy(false); }
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Laden…</div>;

  const newsItems: any[] = Array.isArray(nieuws?.items) ? nieuws.items : [];
  const coostoItems: any[] = Array.isArray(coosto?.items) ? coosto.items : [];
  // Alleen termen tonen die nog NIET in de zoektermen staan — anders lijkt klikken 'niets te doen'.
  const suggesties = [project.name, project.location, (project as any).locationData?.province]
    .filter((x) => x && x !== 'Unknown' && !heeftTerm(String(x)));
  const thumbW = isMobile ? 84 : 116;
  const thumbH = isMobile ? 68 : 88;
  const shownNews = showAllNews ? newsItems : newsItems.slice(0, 6);
  const shownCoosto = showAllCoosto ? coostoItems : coostoItems.slice(0, 6);

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1040, margin: '0 auto' }}>
      <button onClick={() => navigate(`/project/${project.id}/omgevingsanalyse`)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 16, padding: '6px 10px 6px 6px', borderRadius: 8, border: 'none', background: 'transparent', color: '#6b7280', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        Terug naar omgevingsanalyse
      </button>

      <FaseTag nr={2} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span style={{ width: 40, height: 40, borderRadius: 11, background: faseKleur(2).soft, color: '#1e3a2f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v10a2 2 0 0 0 2 2z" /><line x1="6" y1="9" x2="13" y2="9" /><line x1="6" y1="13" x2="13" y2="13" /></svg>
        </span>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1a2e' }}>Nieuws &amp; sentiment</h1>
      </div>
      <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14.5, lineHeight: 1.6 }}>
        Mediamonitoring (Coosto, alle kanalen) en nieuwsberichten over het project, met AI-sentiment. Stel de zoektermen zelf in.
      </p>

      {/* Zoektermen-editor */}
      <div style={{ ...card, marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>Zoektermen</label>
        <div style={{ fontSize: 12.5, color: '#8a90a0', marginBottom: 10 }}>
          {vrij
            ? <>Schrijf je eigen zoekvraag met <strong>OF</strong>, <strong>EN</strong> en <strong>NIET</strong> in hoofdletters (OR, AND en NOT mag ook). Zet woordgroepen tussen aanhalingstekens en groepeer met haakjes.</>
            : <>Kies per term hoe hij meetelt: {OPERATORS.map((o, i) => <React.Fragment key={o.op}>{i > 0 && ' · '}<strong>{o.op}</strong> = {o.uitleg}</React.Fragment>)}. Een woordgroep wordt als geheel gezocht.</>}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {vrij ? (
            <input value={keywords} disabled={locked} onChange={(e) => setKeywords(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy && !locked) zoek(); }}
              placeholder='bijv. (Veerhavens OF "veerdienst Texel") EN veiligheid NIET vacature'
              aria-label="Zoekvraag"
              style={{ flex: 1, minWidth: 220, padding: '10px 12px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, fontFamily: 'inherit', color: '#20242e', background: locked ? '#f6f7f9' : '#fff', boxSizing: 'border-box' }} />
          ) : (
            <div style={{ flex: 1, minWidth: 220, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', borderRadius: 8, border: '1px solid #d8dce4', background: locked ? '#f6f7f9' : '#fff', boxSizing: 'border-box', minHeight: 42 }}>
              {termen.map((t, i) => (
                <React.Fragment key={t.tekst}>
                  {/* De eerste term heeft geen voegwoord nodig, tenzij hij iets anders is dan OF. */}
                  {(i > 0 || t.op !== 'OF') && (locked
                    ? <span style={operatorStijl(t.op)}>{t.op}</span>
                    : (
                      <select value={t.op} onChange={(e) => zetOperator(t.tekst, e.target.value as Operator)}
                        aria-label={`Hoe telt ${t.tekst} mee`} title={OPERATORS.map((o) => `${o.op} = ${o.uitleg}`).join(' · ')}
                        style={{ ...operatorStijl(t.op), cursor: 'pointer' }}>
                        {OPERATORS.map((o) => <option key={o.op} value={o.op}>{o.op}</option>)}
                      </select>
                    ))}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 6px 4px 10px', borderRadius: 999, background: t.op === 'NIET' ? OPERATOR_KLEUR.NIET.vlak : BRAND.papier, border: `1px solid ${t.op === 'NIET' ? OPERATOR_KLEUR.NIET.rand : BRAND.rand}`, fontSize: 13, color: t.op === 'NIET' ? OPERATOR_KLEUR.NIET.tekst : BRAND.groen, fontWeight: 600 }}>
                    {t.tekst}
                    {!locked && (
                      <button type="button" onClick={() => verwijderTerm(t.tekst)} aria-label={`${t.tekst} verwijderen`} title="Verwijderen"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'transparent', color: 'inherit', opacity: 0.6, cursor: 'pointer', padding: 0 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    )}
                  </span>
                </React.Fragment>
              ))}
              {!locked && (
                <input value={nieuweTerm} onChange={(e) => setNieuweTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (nieuweTerm.trim()) { addTerm(nieuweTerm); setNieuweTerm(''); }
                      else if (!busy) zoek();
                    } else if (e.key === 'Backspace' && !nieuweTerm && termen.length) {
                      verwijderTerm(termen[termen.length - 1].tekst);
                    }
                  }}
                  placeholder={termen.length ? 'Term toevoegen…' : 'Typ een term en druk op Enter'}
                  aria-label="Zoekterm toevoegen"
                  style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', fontSize: 14, fontFamily: 'inherit', color: '#20242e', padding: '4px 2px', background: 'transparent' }} />
              )}
            </div>
          )}
          <select value={periode} disabled={busy || locked} onChange={(e) => setPeriode(Number(e.target.value))}
            aria-label="Periode" title="Over welke periode wil je berichten zien?"
            style={{ padding: '10px 10px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, fontFamily: 'inherit', color: '#20242e', background: locked ? '#f6f7f9' : '#fff', cursor: busy || locked ? 'default' : 'pointer', boxSizing: 'border-box', minHeight: 42 }}>
            {PERIODES.map((p) => <option key={p.dagen} value={p.dagen}>{p.label}</option>)}
          </select>
          <button onClick={zoek} disabled={busy || locked}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: '#1e3a2f', color: '#fff', cursor: busy || locked ? 'default' : 'pointer', opacity: busy || locked ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            {busy ? 'Zoeken…' : (nieuws || coosto ? 'Verversen' : 'Zoeken')}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: '#8a90a0' }}>
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }} title={zoekvraag ? `Zo gaat de zoekvraag naar Coosto en Google Nieuws: ${zoekvraag}` : undefined}>Zoekvraag: <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#4b5563' }}>{zoekvraagLeesbaar || '—'}</code></span>
          {!locked && (
            <button type="button" onClick={vrij ? naarTermen : naarVrij}
              style={{ padding: 0, border: 'none', background: 'none', color: BRAND.groen, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {vrij ? 'Terug naar zoektermen' : 'Zelf een zoekvraag schrijven'}
            </button>
          )}
        </div>
        {!locked && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
            {suggesties.length > 0 && <span style={{ fontSize: 12, color: '#9aa1b0' }}>Snel toevoegen:</span>}
            {suggesties.map((t: string) => (
              <button key={t} onClick={() => addTerm(t)} style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, border: '1px solid #e5e7eb', background: '#fff', color: '#4b5563', cursor: 'pointer' }}>+ {t}</button>
            ))}
            <button onClick={stelZoektermenVoor} disabled={aiBusy}
              title="Laat de AI zoektermen voorstellen op basis van de projectverkenning en de omgevingsanalyse"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, border: `1px solid ${BRAND.rand}`, background: BRAND.papier, color: BRAND.groen, cursor: aiBusy ? 'default' : 'pointer', opacity: aiBusy ? 0.6 : 1 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.8 4.6L18.5 9l-4.7 1.4L12 15l-1.8-4.6L5.5 9l4.7-1.4z" /><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" /></svg>
              {aiBusy ? 'AI zoekt…' : (aiSuggesties.length ? 'Opnieuw voorstellen' : 'AI-suggesties')}
            </button>
          </div>
        )}
        {!locked && aiSuggesties.length > 0 && (
          <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: BRAND.papier, border: `1px solid ${BRAND.rand}` }}>
            <div style={{ fontSize: 12, color: BRAND.groenMuted, marginBottom: 6 }}>Voorstellen van de AI — klik om toe te voegen aan je zoektermen:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {aiSuggesties.map((z) => {
                const al = z.term.split(/\s+(?:OR|OF)\s+/).every((d) => heeftTerm(d));
                return (
                  <button key={z.term} onClick={() => addTerm(z.term)} disabled={al} title={z.waarom}
                    style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, border: `1px solid ${al ? '#cdebd6' : BRAND.rand}`, background: al ? '#f2fbf5' : '#fff', color: al ? '#1f7a44' : '#4b5563', cursor: al ? 'default' : 'pointer' }}>
                    {al ? '✓' : '+'} {z.term}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {locked && <div style={{ marginTop: 10, fontSize: 12.5, color: '#1f7a44' }}>De omgevingsanalyse is gevalideerd; heropen de fase om opnieuw te zoeken.</div>}
      </div>

      {!nieuws && !coosto ? (
        <div style={{ ...card, textAlign: 'center', color: '#8a90a0', padding: '48px 24px' }}>
          Nog niets opgehaald. Stel je zoektermen in en klik op “Zoeken”.
        </div>
      ) : (
        <>
          {/* Tabs: Mediamonitoring + Nieuwsberichten */}
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #ececf1', marginBottom: 18 }}>
            {([{ key: 'coosto', label: 'Mediamonitoring', count: coostoItems.length }, { key: 'nieuws', label: 'Nieuwsberichten', count: newsItems.length }] as const).map((t) => {
              const on = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => setActiveTab(t.key)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 16px', border: 'none', background: 'transparent', borderBottom: `2px solid ${on ? '#1e3a2f' : 'transparent'}`, color: on ? '#1e3a2f' : '#6b7280', fontSize: 14.5, fontWeight: 700, cursor: 'pointer', marginBottom: -1 }}>
                  {t.label}
                  <span style={{ fontSize: 12, fontWeight: 700, color: on ? '#1e3a2f' : '#9aa1b0', background: on ? faseKleur(2).soft : '#f1f3f6', borderRadius: 999, padding: '1px 8px' }}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {activeTab === 'nieuws' && (
          <>
          {nieuws && <SentimentHeader snap={nieuws} isMobile={isMobile} />}

          {nieuws?.samenvatting && (
            <div style={{ ...card, borderLeft: `3px solid ${faseKleur(2).base}`, marginBottom: 16, background: faseKleur(2).soft }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#1e3a2f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.6 4.6L18 8l-4.4 1.4L12 14l-1.6-4.6L6 8l4.4-1.4z" /></svg>
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1a1a2e' }}>AI-samenvatting</span>
              </div>
              <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.65 }}>{nieuws.samenvatting}</p>
            </div>
          )}

          {newsItems.length > 0 ? (
            <>
              <div style={{ display: 'grid', gap: 10 }}>
                {shownNews.map((it, i) => (
                  <MessageCard key={i} w={thumbW} h={thumbH}
                    msg={{ image: it.image, favicon: it.favicon, title: it.title, sentiment: it.sentiment, source: it.source, date: it.pubDate, url: it.link }} />
                ))}
              </div>
              {newsItems.length > 6 && (
                <div style={{ textAlign: 'center', marginTop: 14 }}>
                  <button onClick={() => setShowAllNews((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 999, border: '1px solid #d9e2d5', background: '#fff', color: '#1e3a2f', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
                    {showAllNews ? 'Toon minder' : `Toon meer berichten (${newsItems.length - 6})`}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ ...card, color: '#8a90a0', fontSize: 13.5 }}>Geen nieuwsberichten gevonden voor deze zoektermen in de gekozen periode.</div>
          )}
          </>
          )}

          {/* ===== COOSTO (alle kanalen) ===== */}
          {activeTab === 'coosto' && (
          <>
          <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#9aa1b0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: '#1e3a2f', background: faseKleur(2).soft, borderRadius: 999, padding: '2px 9px' }}>Coosto</span>
            Alle kanalen (nieuws, X, Facebook, Instagram, fora, blogs) met sentiment uit Coosto.
            {coosto && typeof coosto.totalReach === 'number' && coosto.totalReach > 0 && (<span>· bereik ± {coosto.totalReach.toLocaleString('nl-NL')}</span>)}
          </p>

          {coostoItems.length > 0 && (() => {
            // Verdeling over kanalen: social eerst, dan nieuws/blogs.
            const tel: Record<string, number> = {};
            coostoItems.forEach((it: any) => { const l = platformInfo(it.platform).label; tel[l] = (tel[l] || 0) + 1; });
            const social = coostoItems.filter((it: any) => platformInfo(it.platform).social).length;
            const volgorde = Object.entries(tel).sort((a, b) => b[1] - a[1]);
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14, fontSize: 12.5, color: '#6b7280' }}>
                <span><strong style={{ color: '#1a1a2e' }}>{social}</strong> van {coostoItems.length} berichten uit social media:</span>
                {volgorde.map(([label, n]) => {
                  const info = Object.values(PLATFORMS).find((p) => p.label === label) || platformInfo(label);
                  return <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: info.kleur }} />{label} {n}</span>;
                })}
              </div>
            );
          })()}

          {coosto && coostoItems.length > 0 && <SentimentHeader snap={coosto} isMobile={isMobile} />}

          {coosto?.samenvatting && coostoItems.length > 0 && (
            <div style={{ ...card, borderLeft: `3px solid ${faseKleur(2).base}`, marginBottom: 16, background: faseKleur(2).soft }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>AI-samenvatting (Coosto)</div>
              <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.65 }}>{coosto.samenvatting}</p>
            </div>
          )}

          {coostoItems.length > 0 ? (
            <>
              <div style={{ display: 'grid', gap: 10 }}>
                {shownCoosto.map((it, i) => (
                  <MessageCard key={i} w={thumbW} h={thumbH}
                    msg={{ image: it.image, favicon: it.favicon, avatar: it.avatar, title: it.text, sentiment: it.sentiment, platform: it.platform, source: it.author || '', date: it.date, reach: it.reach, url: it.url }} />
                ))}
              </div>
              {coostoItems.length > 6 && (
                <div style={{ textAlign: 'center', marginTop: 14 }}>
                  <button onClick={() => setShowAllCoosto((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 999, border: '1px solid #d9e2d5', background: '#fff', color: '#1e3a2f', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
                    {showAllCoosto ? 'Toon minder' : `Toon meer berichten (${coostoItems.length - 6})`}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ ...card, color: '#8a90a0', fontSize: 13.5 }}>
              {coosto?.fetchError ? coosto.fetchError : 'Geen Coosto-berichten gevonden voor deze zoektermen in de gekozen periode.'}
            </div>
          )}
          </>
          )}
        </>
      )}
    </div>
  );
};
